import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext, OrganizationTypes } from "components/GlobalContext";
import { Accordion, Card, Col, Form, Row, Table } from "react-bootstrap";
import IFireDepartment, { IFireDepartmentCreateOrUpdate, IFireDepartmentInitResult } from "models/sql/IFireDepartment";
import IHealthRiskAssessment from "models/sql/IHealthRiskAssessment";
import FireDepartmentService from "services/FireDepartmentService";
import IDealer, { IDealerFilters } from "models/sql/IDealer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import DealerService from "services/DealerService";
import { generateGoogleMapsDirectionsLinkUrl } from "utilities/BrowserUtility";
import { formatDate, formatDateTime } from "utilities/StringUtility";
import { generateLog, getBaseDomain } from "utilities/MiscUtilities";
import CountryChooser from "components/CountryChooser";
import MarkdownEditor from "components/MarkdownEditor";
import { getStatus, getStatusDate } from "utilities/EquipmentUtility";
import DPSEntitySelector from "components/DPSEntitySelector";
import StateChooser from "components/StateChooser";
import ContactViewer from "components/ContactViewer";
import { useBeforeUnload } from "utilities/UIUtilities";
import DetailFooter from "components/DetailFooter";

interface IFireDepartmentsDetailProps {
    baseData?: IFireDepartmentInitResult;
}

const FireDepartmentsDetail = (props: IFireDepartmentsDetailProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    const [dealer, setDealer] = useState<IDealer>();
    globalContextRef.current = globalContext;
    const { id }: any = useParams();
    const [searchParams] = useSearchParams();
    const defs = searchParams.get("defs") || "{}";
    const parsedDefaults = defs ? JSON.parse(defs) : undefined;
    const [fireDepartment, setFireDepartment] = useState<IFireDepartment | null>({
        ... {
            fireDepartmentId: -1,
            dealerId: undefined,
            activeFirefightersCareer: undefined,
            activeFirefightersPaidPerCall: undefined,
            activeFirefightersVolunteer: undefined,
            county: "",
            deptType: "",
            fdid: "",
            fireDeptName: "",
            hqaddr1: "",
            hqaddr2: "",
            hqcity: "",
            hqCountry: "",
            hqfax: "",
            hqphone: "",
            hqstate: "",
            hqzip: "",
            mailAddr1: "",
            mailAddr2: "",
            mailCity: "",
            mailPobox: "",
            mailState: "",
            mailZip: "",
            nonFirefightingCivilian: undefined,
            nonFirefightingVolunteer: undefined,
            numberOfStations: undefined,
            organizationType: "",
            primaryAgencyForEmergencyMgmt: undefined,
            website: "",
            priority: "",
            status: "",
            customerOf: "",
            action: "",
            notes: "",
            mailLatitude: undefined,
            mailLongitude: undefined,
            hqlongitude: undefined,
            hqlatitude: undefined,
            systemDataJson: "",
            emailSuffix: "",
            dashboardEmails: "",
            defaultRoles: "",
            accessRequests: undefined,
            lastAccessRequest: undefined,
            code: undefined,
            facebookUrl: undefined,
            instagramUrl: undefined,
            tiktokUrl: undefined,
            youtubeUrl: undefined,
            twitterUrl: undefined,
            population: undefined,
            specializations: "",
            finance: "",
            lastUpdated: undefined,
            lastUpdatedBy: ""
        }, ...(parsedDefaults ?? {})
    });
    const navigate = useNavigate();
    const [hasFormDataChanged, setHasFormDataChanged] = useState(false);
    const priorities = ["Target", "Contact Made", "Follow-Up Need", "Rep Hand-Off", "Active"];
    const actions = ["AQ Test", "Demo", "Digital Support", "Find Contact", "F2F Meet", "Quote", "Virtual Meet"];
    const statuses = ["Disqualified", "In-Progress", "Needed", "Sent", "Scheduled"];
    const customerOfs = ["TFT", "TFT Health", "Competitor"];

    useBeforeUnload(
        hasFormDataChanged,
        "You have unsaved changes, please save them before leaving the page.");

    useEffect(
        () => {
            (async () => {
                if (id !== undefined) {
                    globalContext.setLoading(true);

                    const accessToken = await globalContext.getAccessTokenAsync();
                    const result = await FireDepartmentService.GetAsync(accessToken, id);

                    if (result.record) {
                        setFireDepartment(result.record);

                        if (result.record.dealerId != null && result.record.dealerId != undefined) {
                            const dealerResult = await DealerService.GetAsync(accessToken, result.record.dealerId);

                            if (dealerResult.record) {
                                setDealer(dealerResult.record);
                            }
                        }
                    }
                    else {
                        setFireDepartment(null);
                        globalContext.showErrorMessage("Unable to load Fire Department");
                    }

                    globalContext.setLoading(false);
                }
            })();
        }, [id]);

    const deleteAsync = async () => {
        if (fireDepartment && fireDepartment.fdid && window.confirm("Are you SURE you want to delete this?")) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();
            await FireDepartmentService.DeleteAsync(accessToken, fireDepartment.fireDepartmentId);
            globalContext.setLoading(false);
            navigate("/Department");
        }
    };

    const calcScore = (hra: IHealthRiskAssessment) => {
        const total = (
            (hra?.stationCommonAreasScore || 0) +
            (hra?.stationLocationFactorScore || 0) +
            (hra?.stationPpegearScore || 0) +
            (hra?.incidentEquipmentHandlingScore || 0) +
            (hra?.incidentGrossDeconScore || 0) +
            (hra?.incidentProtectiveEquipmentScore || 0) +
            (hra?.apparatusPpegearScore || 0) +
            (hra?.apparatusVehicleDeconScore || 0) +
            (hra?.apparatusVehicleFactorsScore || 0) +
            (hra?.teamHealthWellnessScore || 0) +
            (hra?.teamPovuseScore || 0) +
            (hra?.teamRiskAwarenessScore || 0));

        return (
            Math.ceil((total / ((12 * 3) * 3)) * 100) + "%"
        );
    };

    const submitFormAsync = async () => {
        if (fireDepartment) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();

            const data: IFireDepartmentCreateOrUpdate = {
                fireDepartmentId: fireDepartment.fireDepartmentId,
                dealerId: fireDepartment.dealerId,
                activeFirefightersCareer: fireDepartment.activeFirefightersCareer,
                activeFirefightersPaidPerCall: fireDepartment.activeFirefightersPaidPerCall,
                activeFirefightersVolunteer: fireDepartment.activeFirefightersVolunteer,
                county: fireDepartment.county,
                deptType: fireDepartment.deptType,
                fdid: fireDepartment.fdid,
                fireDeptName: fireDepartment.fireDeptName,
                hqaddr1: fireDepartment.hqaddr1,
                hqaddr2: fireDepartment.hqaddr2,
                hqcity: fireDepartment.hqcity,
                hqCountry: fireDepartment.hqCountry,
                hqfax: fireDepartment.hqfax,
                hqphone: fireDepartment.hqphone,
                hqstate: fireDepartment.hqstate,
                hqzip: fireDepartment.hqzip,
                mailAddr1: fireDepartment.mailAddr1,
                mailAddr2: fireDepartment.mailAddr2,
                mailCity: fireDepartment.mailCity,
                mailPobox: fireDepartment.mailPobox,
                mailState: fireDepartment.mailState,
                mailZip: fireDepartment.mailZip,
                nonFirefightingCivilian: fireDepartment.nonFirefightingCivilian,
                nonFirefightingVolunteer: fireDepartment.nonFirefightingVolunteer,
                numberOfStations: fireDepartment.numberOfStations,
                organizationType: fireDepartment.organizationType,
                primaryAgencyForEmergencyMgmt: fireDepartment.primaryAgencyForEmergencyMgmt,
                website: fireDepartment.website,
                priority: fireDepartment.priority,
                status: fireDepartment.status,
                customerOf: fireDepartment.customerOf,
                action: fireDepartment.action,
                notes: fireDepartment.notes,
                mailLongitude: fireDepartment.mailLongitude,
                mailLatitude: fireDepartment.mailLatitude,
                systemDataJson: fireDepartment.systemDataJson,
                hqlatitude: fireDepartment.hqlatitude,
                hqlongitude: fireDepartment.hqlongitude,
                emailSuffix: fireDepartment.emailSuffix,
                dashboardEmails: fireDepartment.dashboardEmails,
                defaultRoles: fireDepartment.defaultRoles,
                accessRequests: fireDepartment.accessRequests,
                lastAccessRequest: fireDepartment.lastAccessRequest,
                code: fireDepartment.code,
                facebookUrl: fireDepartment.facebookUrl,
                instagramUrl: fireDepartment.instagramUrl,
                tiktokUrl: fireDepartment.tiktokUrl,
                youtubeUrl: fireDepartment.youtubeUrl,
                twitterUrl: fireDepartment.twitterUrl,
                population: fireDepartment.population,
                specializations: fireDepartment.specializations,
                finance: fireDepartment.finance,
                lastUpdated: fireDepartment.lastUpdated,
                lastUpdatedBy: fireDepartment.lastUpdatedBy
            };

            await FireDepartmentService.CreateOrUpdateAsync(accessToken, data);
            globalContext.setLoading(false);
            setHasFormDataChanged(false);

            if (data.fireDepartmentId === -1) {
                navigate(-1);
            }
        }
    };

    if (fireDepartment) {
        return (
            <>
                <Row>
                    <Col>
                        <Card className="mb-2 mt-2">
                            <Card.Body>
                                <Card.Title>
                                    {
                                        id !== undefined &&
                                        <>
                                            <strong>{fireDepartment.fireDeptName} ({fireDepartment.fdid})&nbsp;
                                                <FontAwesomeIcon
                                                    icon={faUpRightFromSquare as IconProp}
                                                    style={{ cursor: "pointer" }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        window.open(generateGoogleMapsDirectionsLinkUrl(`${fireDepartment.hqaddr1} ${fireDepartment.hqcity} ${fireDepartment.hqstate}, ${fireDepartment.hqzip} ${fireDepartment.hqCountry}`))
                                                    }} /></strong><br />
                                            {
                                                fireDepartment.code &&
                                                <>
                                                    <a
                                                        href={`https://${getBaseDomain()}/Public/HRAReport?dc=${fireDepartment.code}`}
                                                        target="_blank">View Department HRA Rollup Report</a><br />

                                                    <a
                                                        href={`https://${getBaseDomain()}/Public/HRA?dc=${fireDepartment.code}`}
                                                        target="_blank">Start Department HRA</a>
                                                </>
                                            }
                                            {
                                                !fireDepartment.code &&
                                                <strong style={{ color: "red" }}>Department does not have a code</strong>
                                            }
                                        </>
                                    }
                                    {
                                        id === undefined &&
                                        <strong>New Fire Department</strong>
                                    }
                                </Card.Title>

                                <DPSEntitySelector<IDealer, IDealerFilters>
                                    entityFilterSearchPropName={"dealerNames"}
                                    entityService={DealerService}
                                    entityTypeName="Dealer"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={dealer}
                                    generateEntityTitle={(entry) => entry.dealerName ?? ""}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/Dealers/Detail/${entry.dealerId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setFireDepartment({ ...fireDepartment, dealerId: newEntry?.dealerId || undefined });
                                        setDealer(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, fireDeptName: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.fireDeptName}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Engagement</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={async (e) => {
                                                        setFireDepartment({ ...fireDepartment, priority: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={fireDepartment.priority}>
                                                    <option key={"empty"} value={""}>-</option>
                                                    {
                                                        priorities.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                    }
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Action</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={async (e) => {
                                                        setFireDepartment({ ...fireDepartment, action: e.target.value });
                                                    }}
                                                    value={fireDepartment.action}>
                                                    <option key={"empty"} value={""}>-</option>
                                                    {
                                                        actions.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                    }
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Status</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={async (e) => {
                                                        setFireDepartment({ ...fireDepartment, status: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={fireDepartment.status}>
                                                    <option key={"empty"} value={""}>-</option>
                                                    {
                                                        statuses.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                    }
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>HQ Addr 1</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, hqaddr1: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.hqaddr1}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>HQ Addr 2</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, hqaddr2: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.hqaddr2}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>HQ City</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, hqcity: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.hqcity}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>HQ Country</Form.Label>
                                            <CountryChooser
                                                onChange={newVal => {
                                                    setFireDepartment({ ...fireDepartment, hqCountry: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                defaultValue={fireDepartment.hqCountry}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>HQ State</Form.Label>
                                            {
                                                <StateChooser
                                                    onChange={newVal => {
                                                        setFireDepartment({ ...fireDepartment, hqstate: newVal });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    defaultValue={fireDepartment.hqstate}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>HQ Zip</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, hqzip: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.hqzip}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>County</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, county: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.county}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>HQ Phone</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, hqphone: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.hqphone}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                {/*
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Mail Addr 1</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, mailAddr1: e.target.value });
                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.mailAddr1}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Mail Addr 2</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, mailAddr2: e.target.value });
                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.mailAddr2}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Mail City</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, mailCity: e.target.value });
                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.mailCity}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Mail State</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, mailState: e.target.value });
                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.mailState}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Mail Zip</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, mailZip: e.target.value });
                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.mailZip}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Mail Po Box</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, mailPobox: e.target.value });
                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.mailPobox}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                */}
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Dept Type</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={async (e) => {
                                                        setFireDepartment({ ...fireDepartment, deptType: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={fireDepartment.deptType}>
                                                    <option key={"empty"} value={""}>None</option>
                                                    {
                                                        [
                                                            "Mostly career",
                                                            "Volunteer",
                                                            "Career",
                                                            "Mostly volunteer"
                                                        ].map(s => <option key={s} value={s}>{s}</option>)
                                                    }
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Specializations</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, specializations: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.specializations}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Number Of Stations</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, numberOfStations: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.numberOfStations || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Population Served</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, population: parseInt(e.target.value) });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.population}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Finance Type</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, finance: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.finance}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Organization Type</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={async (e) => {
                                                        setFireDepartment({ ...fireDepartment, organizationType: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={fireDepartment.organizationType}>
                                                    <option key={"empty"} value={""}>None</option>
                                                    {
                                                        OrganizationTypes.map(s => <option key={s} value={s}>{s}</option>)
                                                    }
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Primary Agency For Emergency Mgmt</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={async (e) => {
                                                        setFireDepartment({ ...fireDepartment, primaryAgencyForEmergencyMgmt: e.target.value == "true" });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={fireDepartment.primaryAgencyForEmergencyMgmt ? "true" : "false"}>
                                                    <option key={"empty"} value={""}>-</option>
                                                    {
                                                        ["true", "false"].map((s, index) => <option key={s} value={s}>{s}</option>)
                                                    }
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Active Firefighters Career</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, activeFirefightersCareer: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.activeFirefightersCareer || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Active Firefighters Volunteer</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, activeFirefightersVolunteer: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.activeFirefightersVolunteer || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Email Suffix<br /><i>MUST include @ symbol.</i></Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, emailSuffix: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.emailSuffix}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Code</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, code: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.code}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>HQ Latitude</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, hqlatitude: parseFloat(e.target.value) });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.hqlatitude}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>HQ Longitude</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, hqlongitude: parseFloat(e.target.value) });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.hqlongitude}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Access Requests</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={true}
                                                    value={fireDepartment.accessRequests ?? "0"}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Last Access Request</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={true}
                                                    value={formatDate(fireDepartment.lastAccessRequest)}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Dashboard Email Access</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, dashboardEmails: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.dashboardEmails}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Default Roles</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, defaultRoles: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireDepartment.defaultRoles}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row>
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Last Updated</Form.Label>
                                                {
                                                    <Form.Control
                                                        type="text"
                                                        readOnly
                                                        value={formatDate(fireDepartment.lastUpdated)}
                                                    />
                                                }
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Last Updated By</Form.Label>
                                                {
                                                    <Form.Control
                                                        type="text"
                                                        readOnly
                                                        value={fireDepartment.lastUpdatedBy}
                                                    />
                                                }
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                }
                                <Row className="mb-3">
                                    <Col>
                                        <Form.Label><h2>Customer Of</h2></Form.Label><br />
                                        <Form.Check
                                            inline
                                            type="radio"
                                            label={"None"}
                                            value={""}
                                            checked={!fireDepartment.customerOf}
                                            onChange={
                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setFireDepartment({ ...fireDepartment, customerOf: e.target.value });
                                                    setHasFormDataChanged(true);
                                                }
                                            }
                                        />
                                        {
                                            customerOfs.map((c, index) =>
                                                <Form.Check
                                                    inline
                                                    type="radio"
                                                    label={c}
                                                    value={c}
                                                    checked={fireDepartment.customerOf === c}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFireDepartment({ ...fireDepartment, customerOf: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                />)
                                        }
                                    </Col>
                                </Row>

                                <Row className="pb-3">
                                    <Col>
                                        <Accordion style={{ border: "1px solid #0d6efd", borderRadius: "0.375rem" }}>
                                            <Accordion.Item
                                                eventKey="0">
                                                <Accordion.Header><strong>Website and pages</strong></Accordion.Header>
                                                <Accordion.Body>
                                                    <Row>
                                                        <Col>
                                                            <Form.Group className="mb-3" >
                                                                <Form.Label>
                                                                    <a href={fireDepartment.website} target="_blank">
                                                                        Website&nbsp;
                                                                        <FontAwesomeIcon
                                                                            icon={faUpRightFromSquare as IconProp}
                                                                            style={{ cursor: "pointer" }} />
                                                                    </a>
                                                                </Form.Label>
                                                                {
                                                                    <Form.Control
                                                                        type="text"
                                                                        onChange={
                                                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                setFireDepartment({ ...fireDepartment, website: e.target.value });
                                                                                setHasFormDataChanged(true);
                                                                            }
                                                                        }
                                                                        value={fireDepartment.website}
                                                                    />
                                                                }
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col>
                                                            <Form.Group className="mb-3" >
                                                                <Form.Label>
                                                                    <a href={fireDepartment.facebookUrl} target="_blank">
                                                                        Facebook&nbsp;
                                                                        <FontAwesomeIcon
                                                                            icon={faUpRightFromSquare as IconProp}
                                                                            style={{ cursor: "pointer" }} />
                                                                    </a>
                                                                </Form.Label>
                                                                {
                                                                    <Form.Control
                                                                        type="text"
                                                                        onChange={
                                                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                setFireDepartment({ ...fireDepartment, facebookUrl: e.target.value });
                                                                                setHasFormDataChanged(true);
                                                                            }
                                                                        }
                                                                        value={fireDepartment.facebookUrl}
                                                                    />
                                                                }
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col>
                                                            <Form.Group className="mb-3" >
                                                                <Form.Label>
                                                                    <a href={fireDepartment.instagramUrl} target="_blank">
                                                                        Instagram&nbsp;
                                                                        <FontAwesomeIcon
                                                                            icon={faUpRightFromSquare as IconProp}
                                                                            style={{ cursor: "pointer" }} />
                                                                    </a>
                                                                </Form.Label>
                                                                {
                                                                    <Form.Control
                                                                        type="text"
                                                                        onChange={
                                                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                setFireDepartment({ ...fireDepartment, instagramUrl: e.target.value });
                                                                                setHasFormDataChanged(true);
                                                                            }
                                                                        }
                                                                        value={fireDepartment.instagramUrl}
                                                                    />
                                                                }
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col>
                                                            <Form.Group className="mb-3" >
                                                                <Form.Label>
                                                                    <a href={fireDepartment.youtubeUrl} target="_blank">
                                                                        Youtube&nbsp;
                                                                        <FontAwesomeIcon
                                                                            icon={faUpRightFromSquare as IconProp}
                                                                            style={{ cursor: "pointer" }} />
                                                                    </a>
                                                                </Form.Label>
                                                                {
                                                                    <Form.Control
                                                                        type="text"
                                                                        onChange={
                                                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                setFireDepartment({ ...fireDepartment, youtubeUrl: e.target.value });
                                                                                setHasFormDataChanged(true);
                                                                            }
                                                                        }
                                                                        value={fireDepartment.youtubeUrl}
                                                                    />
                                                                }
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col>
                                                            <Form.Group className="mb-3" >
                                                                <Form.Label>
                                                                    <a href={fireDepartment.twitterUrl} target="_blank">
                                                                        Twitter / X&nbsp;
                                                                        <FontAwesomeIcon
                                                                            icon={faUpRightFromSquare as IconProp}
                                                                            style={{ cursor: "pointer" }} />
                                                                    </a>
                                                                </Form.Label>
                                                                {
                                                                    <Form.Control
                                                                        type="text"
                                                                        onChange={
                                                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                setFireDepartment({ ...fireDepartment, twitterUrl: e.target.value });
                                                                                setHasFormDataChanged(true);
                                                                            }
                                                                        }
                                                                        value={fireDepartment.twitterUrl}
                                                                    />
                                                                }
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col>
                                                            <Form.Group className="mb-3" >
                                                                <Form.Label>
                                                                    <a href={fireDepartment.tiktokUrl} target="_blank">
                                                                        Tiktok&nbsp;
                                                                        <FontAwesomeIcon
                                                                            icon={faUpRightFromSquare as IconProp}
                                                                            style={{ cursor: "pointer" }} />
                                                                    </a>
                                                                </Form.Label>
                                                                {
                                                                    <Form.Control
                                                                        type="text"
                                                                        onChange={
                                                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                                setFireDepartment({ ...fireDepartment, tiktokUrl: e.target.value });
                                                                                setHasFormDataChanged(true);
                                                                            }
                                                                        }
                                                                        value={fireDepartment.tiktokUrl}
                                                                    />
                                                                }
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>
                                    </Col>
                                </Row>

                                {
                                    globalContext.roles.some(r => r === "Web.Contacts") &&
                                    <ContactViewer
                                        source="FireDepartment"
                                        service={FireDepartmentService}
                                        entityId={fireDepartment.fireDepartmentId}
                                        canEdit={globalContext.roles.some(r => r === "Web.Admin")}
                                    />
                                }

                                {
                                    globalContext.roles.some(r => r === "Web.SalesRep") &&
                                    <Row className="pb-3">
                                        <Col>
                                            <Accordion style={{ border: "1px solid #0d6efd", borderRadius: "0.375rem" }}>
                                                <Accordion.Item
                                                    eventKey="0">
                                                    <Accordion.Header><strong>Reps</strong></Accordion.Header>
                                                    <Accordion.Body>
                                                        <Table className="lookup-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Name</th>
                                                                    <th>Email</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {
                                                                    fireDepartment.salesReps?.map((e, index) =>
                                                                        <tr
                                                                            key={index}
                                                                            style={{ cursor: "pointer" }}
                                                                            onClick={(event) => {
                                                                                event.preventDefault();
                                                                                event.stopPropagation();
                                                                                window.open(`/Reps/Detail/${e.salesRepId}`)
                                                                            }}><td>
                                                                                <FontAwesomeIcon icon={faUpRightFromSquare as IconProp} /> {e.fullName}
                                                                            </td>
                                                                            <td>
                                                                                {e.email}
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                }
                                                            </tbody>
                                                        </Table>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion>
                                        </Col>
                                    </Row>
                                }

                                {
                                    globalContext.roles.some(r => r === "Web.Equipment") &&
                                    <Row className="pb-3">
                                        <Col>
                                            <Accordion style={{ border: "1px solid #0d6efd", borderRadius: "0.375rem" }}>
                                                <Accordion.Item
                                                    eventKey="0">
                                                    <Accordion.Header><strong>Equipment</strong></Accordion.Header>
                                                    <Accordion.Body>
                                                        <Table className="lookup-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Product</th>
                                                                    <th>Serial</th>
                                                                    <th>Demo Requested</th>
                                                                    <th>Demo Started</th>
                                                                    <th>Registered</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {
                                                                    fireDepartment.equipment?.filter(e => !(e.serialNumber ?? "").toLowerCase().startsWith("r01") && !(e.serialNumber ?? "").toLowerCase().startsWith("d01")).map((e, index) =>
                                                                        <tr
                                                                            key={index}
                                                                            style={{ cursor: "pointer" }}
                                                                            className={(e.demoRequestDate && !e.demoStartDate) ? "table-action-required" : ""}
                                                                            onClick={(event) => {
                                                                                event.preventDefault();
                                                                                event.stopPropagation();
                                                                                window.open(`/Equipment/Detail/${e.equipmentId}`)
                                                                            }}><td>
                                                                                <FontAwesomeIcon icon={faUpRightFromSquare as IconProp} /> {e.product?.name}
                                                                            </td>
                                                                            <td>
                                                                                {e.serialNumber}
                                                                            </td>
                                                                            <td>
                                                                                {formatDateTime(e.demoRequestDate)}
                                                                            </td>
                                                                            <td>
                                                                                {formatDateTime(e.demoStartDate)}
                                                                            </td>
                                                                            <td>
                                                                                {formatDateTime(e.registrationDate)}
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                }
                                                            </tbody>
                                                        </Table>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion>
                                        </Col>
                                    </Row>
                                }

                                {
                                    globalContext.roles.some(r => r === "Web.Equipment") &&
                                    <Row className="pb-3">
                                        <Col>
                                            <Accordion style={{ border: "1px solid #0d6efd", borderRadius: "0.375rem" }}>
                                                <Accordion.Item
                                                    eventKey="0">
                                                    <Accordion.Header><strong>Aircards</strong></Accordion.Header>
                                                    <Accordion.Body>
                                                        <Table className="lookup-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Product</th>
                                                                    <th>Serial</th>
                                                                    <th>Status</th>
                                                                    <th>Status Date</th>
                                                                    <th>Score</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {
                                                                    fireDepartment.equipment?.filter(e => (e.serialNumber ?? "").toLowerCase().startsWith("r01") || (e.serialNumber ?? "").toLowerCase().startsWith("d01")).map((e, index) =>
                                                                        <tr
                                                                            key={index}
                                                                            style={{ cursor: "pointer" }}
                                                                            className={(e.demoRequestDate && !e.demoStartDate) ? "table-action-required" : ""}
                                                                            onClick={(event) => {
                                                                                event.preventDefault();
                                                                                event.stopPropagation();
                                                                                window.open(`/AirCards/Detail/${e.equipmentId}`)
                                                                            }}><td>
                                                                                <FontAwesomeIcon icon={faUpRightFromSquare as IconProp} /> {e.product?.name}
                                                                            </td>
                                                                            <td>
                                                                                {e.serialNumber}
                                                                            </td>
                                                                            <td>
                                                                                {getStatus(e)}
                                                                            </td>
                                                                            <td>
                                                                                {getStatusDate(e)}
                                                                            </td>
                                                                            <td>
                                                                                {e.score}
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                }
                                                            </tbody>
                                                        </Table>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion>
                                        </Col>
                                    </Row>
                                }

                                {
                                    globalContext.roles.some(r => r === "Web.HealthRiskAssessments") &&

                                    <Row className="pb-3">
                                        <Col>
                                            <Accordion style={{ border: "1px solid #0d6efd", borderRadius: "0.375rem" }}>
                                                <Accordion.Item
                                                    eventKey="0">
                                                    <Accordion.Header><strong>HRAs</strong></Accordion.Header>
                                                    <Accordion.Body>
                                                        <Table>
                                                            <thead>
                                                                <tr>
                                                                    <th>Submission Date</th>
                                                                    <th>Total Score</th>
                                                                    <th>Station</th>
                                                                    <th>Shift</th>
                                                                    <th>Name</th>
                                                                    <th>Email</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {
                                                                    fireDepartment.healthRiskAssessments?.map((e, index) =>
                                                                        <tr
                                                                            key={index}
                                                                            style={{ cursor: "pointer" }}
                                                                            onClick={(event) => {
                                                                                event.preventDefault();
                                                                                event.stopPropagation();
                                                                                window.open(`/HealthRiskAssessments/Detail/${e.healthRiskAssessmentId}`)
                                                                            }}>
                                                                            <td>
                                                                                <FontAwesomeIcon icon={faUpRightFromSquare as IconProp} /> {formatDate(e.submissionDate)}
                                                                            </td>
                                                                            <td>
                                                                                {
                                                                                    calcScore(e)
                                                                                }
                                                                            </td>
                                                                            <td>
                                                                                {e.stationNumber}
                                                                            </td>
                                                                            <td>
                                                                                {e.shift}
                                                                            </td>
                                                                            <td>
                                                                                {e.userName}
                                                                            </td>
                                                                            <td>
                                                                                {e.userEmail}
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                }
                                                            </tbody>
                                                        </Table>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion>
                                        </Col>
                                    </Row>
                                }

                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Admin Notes</Form.Label>
                                            <MarkdownEditor
                                                noun="Note"
                                                onChange={newVal => {
                                                    setFireDepartment({ ...fireDepartment, notes: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={fireDepartment.notes || ""}
                                            />
                                        </Form.Group>
                                    </Row>
                                }
                            </Card.Body>
                        </Card>
                    </Col >
                </Row >
                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <Row className="align-items-end">
                        <Col>
                            <Card className="mb-2">
                                <Card.Body>
                                    <Card.Title>
                                        <strong>Log</strong><br />
                                    </Card.Title>
                                    {generateLog<IFireDepartment>(fireDepartment)}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                }

                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <DetailFooter
                        hasFormDataChanged={hasFormDataChanged}
                        deleteAsync={deleteAsync}
                        submitFormAsync={submitFormAsync}
                    />
                }
            </>
        );
    }
    else {
        return (
            <>
                <h1>There was an issue retreiving the Fire Department, please try again.</h1>
            </>
        );
    }
}

export default FireDepartmentsDetail;
